/**
 * Humanize audit actions and entity types into friendly bilingual labels.
 *
 * The pure functions here look up the action / entity in the i18n string
 * table; if a translation is missing they fall back to a sensible
 * humanized form of the raw key (e.g. "HOUSEHOLD_MEMBER_UPDATED" ->
 * "Household member updated").
 */
import { STRINGS, type StringKey } from '@/i18n';

export type Lang = 'en' | 'es';

function fallbackHumanize(s: string): string {
  const lower = s.toLowerCase().replace(/_/g, ' ').trim();
  if (!lower) return '';
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function humanizeAction(action: string | null | undefined, lang: Lang): string {
  if (!action) return '';
  const key = ('admin.audit.action.' + action) as StringKey;
  const entry = (STRINGS as Record<string, { en: string; es: string } | undefined>)[key];
  if (entry) return entry[lang] ?? entry.en;
  return fallbackHumanize(action);
}

export function humanizeEntity(entityType: string | null | undefined, lang: Lang): string {
  if (!entityType) return '';
  const key = ('admin.audit.entity.' + entityType) as StringKey;
  const entry = (STRINGS as Record<string, { en: string; es: string } | undefined>)[key];
  if (entry) return entry[lang] ?? entry.en;
  return fallbackHumanize(entityType);
}
